import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    CircularProgress,
    InputAdornment,
    Divider,
    useTheme,
    useMediaQuery,
    Tooltip,
    Alert,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import {
    subscribeWhatsappChats,
    subscribeWhatsappMessages,
    pushAdminMediaMessage,
} from '../../../services/whatsappRealtimeService';
import { COLORS } from '../../../assets/colors';

function roleMeta(role) {
    const r = String(role || '').toLowerCase();
    if (r === 'admin') {
        return { label: 'Admin', align: 'right', Icon: SupportAgentIcon, variant: 'admin' };
    }
    if (r === 'ia' || r === 'ai') {
        return { label: 'IA', align: 'left', Icon: SmartToyIcon, variant: 'ai' };
    }
    return { label: 'Cliente', align: 'left', Icon: PersonIcon, variant: 'client' };
}

function formatChatTime(seconds) {
    const n = parseInt(String(seconds), 10);
    if (!n) return '';
    const d = new Date(n * 1000);
    return d.toLocaleString('es-CO', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function Chat() {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedImagePreview, setSelectedImagePreview] = useState('');
    const listEndRef = useRef(null);
    const imageInputRef = useRef(null);

    const clearSelectedImage = useCallback(() => {
        setSelectedImageFile(null);
        setSelectedImagePreview('');
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    }, []);

    const scrollToBottom = useCallback(() => {
        listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const unsub = subscribeWhatsappChats(setChats);
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setMessages([]);
            return undefined;
        }
        const unsub = subscribeWhatsappMessages(selectedId, setMessages);
        return () => unsub();
    }, [selectedId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, selectedId, scrollToBottom]);

    const handleSend = async () => {
        if (!selectedId || sending) return;
        const trimmedDraft = draft.trim();
        if (!trimmedDraft && !selectedImageFile) return;
        setSendError('');
        setSending(true);
        try {
            await pushAdminMediaMessage(selectedId, {
                text: trimmedDraft,
                imageFile: selectedImageFile,
            });
            setDraft('');
            clearSelectedImage();
        } catch (e) {
            setSendError(
                e?.message ||
                    'No se pudo enviar. Verifica las reglas de Firebase (Realtime Database) para usuarios autenticados.'
            );
        } finally {
            setSending(false);
        }
    };

    const handleImagePick = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setSendError('Solo se permiten archivos de imagen');
            clearSelectedImage();
            return;
        }
        setSendError('');
        setSelectedImageFile(file);
        setSelectedImagePreview(URL.createObjectURL(file));
    };

    const showList = !isNarrow || !selectedId;
    const showThread = !isNarrow || selectedId;

    return (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    fontFamily: 'Quicksand',
                    fontWeight: 'bold',
                    color: COLORS.primary,
                    marginBottom: 3,
                }}
            >
                Chat WhatsApp
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    height: { xs: 'calc(100vh - 180px)', md: 'calc(100vh - 200px)' },
                    minHeight: 420,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${COLORS.secondary}35`,
                    boxShadow: '0 4px 24px rgba(30, 58, 95, 0.08)',
                }}
            >
                {showList && (
                    <Box
                        sx={{
                            width: { xs: '100%', md: 300 },
                            flexShrink: 0,
                            borderRight: { md: `1px solid ${COLORS.secondary}30` },
                            display: 'flex',
                            flexDirection: 'column',
                            background:
                                'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.75,
                                background: `linear-gradient(135deg, ${COLORS.primary} 0%, rgba(30, 58, 95, 0.92) 100%)`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Quicksand',
                                    fontWeight: 700,
                                    color: COLORS.secondary,
                                    fontSize: '0.95rem',
                                }}
                            >
                                Conversaciones
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Quicksand',
                                    color: 'rgba(255,255,255,0.75)',
                                    fontSize: '0.75rem',
                                    mt: 0.25,
                                }}
                            >
                                {chats.length} contacto{chats.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <List
                            dense
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                py: 0,
                            }}
                        >
                            {chats.length === 0 ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <ChatIcon
                                        sx={{ fontSize: 40, color: COLORS.gray, opacity: 0.4, mb: 1 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            color: COLORS.gray,
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Aún no hay conversaciones en{' '}
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                            /whatsapp
                                        </Box>
                                    </Typography>
                                </Box>
                            ) : (
                                chats.map((c) => (
                                    <ListItemButton
                                        key={c.id}
                                        selected={selectedId === c.id}
                                        onClick={() => setSelectedId(c.id)}
                                        sx={{
                                            borderBottom: `1px solid ${COLORS.secondary}18`,
                                            '&.Mui-selected': {
                                                backgroundColor: `${COLORS.secondary}18`,
                                                borderLeft: `3px solid ${COLORS.secondary}`,
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontWeight: 700,
                                                        color: COLORS.primary,
                                                        fontSize: '0.9rem',
                                                    }}
                                                >
                                                    +{c.id}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        fontSize: '0.8rem',
                                                        color: '#666',
                                                        mt: 0.25,
                                                    }}
                                                >
                                                    {c.preview || 'Sin mensajes'}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                ))
                            )}
                        </List>
                    </Box>
                )}

                {showThread && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth: 0,
                            background: '#f0f2f5',
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                background: '#fff',
                                borderBottom: `1px solid ${COLORS.secondary}25`,
                            }}
                        >
                            {isNarrow && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedId('')}
                                    sx={{ color: COLORS.primary }}
                                    aria-label="Volver al listado"
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {selectedId ? (
                                    <>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontWeight: 700,
                                                color: COLORS.primary,
                                            }}
                                        >
                                            +{selectedId}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                color: COLORS.gray,
                                            }}
                                        >
                                            Mensajes en tiempo real
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            color: COLORS.gray,
                                        }}
                                    >
                                        Selecciona un contacto
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25,
                            }}
                        >
                            {!selectedId && (
                                <Box
                                    sx={{
                                        m: 'auto',
                                        textAlign: 'center',
                                        maxWidth: 280,
                                    }}
                                >
                                    <ChatIcon
                                        sx={{
                                            fontSize: 48,
                                            color: COLORS.secondary,
                                            opacity: 0.5,
                                            mb: 1,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            color: COLORS.gray,
                                        }}
                                    >
                                        Elige una conversación en la lista para ver el historial y
                                        responder como administrador.
                                    </Typography>
                                </Box>
                            )}

                            {selectedId &&
                                messages.map((m) => {
                                    const meta = roleMeta(m.role);
                                    const isRight = meta.align === 'right';
                                    const bubbleBg =
                                        meta.variant === 'admin'
                                            ? `linear-gradient(135deg, ${COLORS.primary} 0%, #2a4a73 100%)`
                                            : meta.variant === 'ai'
                                              ? 'linear-gradient(135deg, #e8f4f8 0%, #dceef5 100%)'
                                              : '#ffffff';
                                    const textColor =
                                        meta.variant === 'admin' ? '#fff' : COLORS.primary;
                                    const subColor =
                                        meta.variant === 'admin'
                                            ? 'rgba(255,255,255,0.85)'
                                            : COLORS.gray;

                                    return (
                                        <Box
                                            key={m.key}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: isRight
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                            }}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    maxWidth: '78%',
                                                    px: 1.75,
                                                    py: 1.25,
                                                    borderRadius: 2,
                                                    background: bubbleBg,
                                                    border:
                                                        meta.variant === 'client'
                                                            ? `1px solid ${COLORS.secondary}22`
                                                            : meta.variant === 'ai'
                                                              ? `1px solid ${COLORS.info}44`
                                                              : 'none',
                                                    boxShadow:
                                                        meta.variant === 'admin'
                                                            ? '0 2px 8px rgba(30,58,95,0.25)'
                                                            : '0 1px 2px rgba(0,0,0,0.06)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <meta.Icon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: subColor,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontFamily: 'Quicksand',
                                                            fontWeight: 700,
                                                            color: subColor,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                        }}
                                                    >
                                                        {meta.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontFamily: 'Quicksand',
                                                            color: subColor,
                                                            ml: 'auto',
                                                        }}
                                                    >
                                                        {formatChatTime(m.time)}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Quicksand',
                                                        color: textColor,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {m.text}
                                                </Typography>
                                                {m.image && (
                                                    <Box sx={{ mt: m.text ? 1 : 0 }}>
                                                        <Box
                                                            component="img"
                                                            src={m.image}
                                                            alt="Imagen de chat"
                                                            sx={{
                                                                maxWidth: 280,
                                                                width: '100%',
                                                                borderRadius: 1.5,
                                                                objectFit: 'cover',
                                                                border:
                                                                    meta.variant === 'admin'
                                                                        ? '1px solid rgba(255,255,255,0.3)'
                                                                        : `1px solid ${COLORS.secondary}33`,
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Box>
                                    );
                                })}
                            <div ref={listEndRef} />
                        </Box>

                        {selectedId && (
                            <>
                                <Divider />
                                {sendError && (
                                    <Alert severity="error" sx={{ borderRadius: 0 }}>
                                        {sendError}
                                    </Alert>
                                )}
                                <Box
                                    sx={{
                                        p: 2,
                                        background: '#fff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.25,
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    {selectedImagePreview && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    border: `1px solid ${COLORS.secondary}35`,
                                                    borderRadius: 1.5,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={selectedImagePreview}
                                                    alt="Previsualización"
                                                    sx={{
                                                        height: 90,
                                                        width: 90,
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={clearSelectedImage}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        bgcolor: 'rgba(0,0,0,0.5)',
                                                        color: '#fff',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0,0,0,0.7)',
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-end' }}>
                                        <input
                                            ref={imageInputRef}
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleImagePick}
                                        />
                                        <Tooltip title="Adjuntar imagen">
                                            <span>
                                                <IconButton
                                                    onClick={() => imageInputRef.current?.click()}
                                                    disabled={sending}
                                                    sx={{
                                                        color: COLORS.primary,
                                                        border: `1px solid ${COLORS.secondary}50`,
                                                        borderRadius: 1.5,
                                                    }}
                                                >
                                                    <ImageIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    <TextField
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        placeholder="Escribe un mensaje como administrador…"
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        disabled={sending}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSend();
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                fontFamily: 'Quicksand',
                                                borderRadius: 2,
                                            },
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {sending ? (
                                                        <CircularProgress size={22} />
                                                    ) : (
                                                        <Tooltip title="Enviar">
                                                            <span>
                                                                <IconButton
                                                                    color="primary"
                                                                    onClick={handleSend}
                                                                    disabled={!draft.trim() && !selectedImageFile}
                                                                    sx={{
                                                                        color: COLORS.primary,
                                                                    }}
                                                                >
                                                                    <SendIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default Chat;
